import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Upload, Plus, Trash2, Edit, X, Image as ImageIcon, CheckCircle2, RotateCcw, Eye, EyeOff, KeyRound, FileText, BadgeCheck } from "lucide-react";
import { api, imageUrl, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [profile, setProfile] = useState({ name: "", bio: "", photo_path: "", statement: "", hero_image_path: "" });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, p, pr] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/products"),
        api.get("/artist-profile"),
      ]);
      setOrders(o.data);
      setProducts(p.data);
      setProfile(pr.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this artwork? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Artwork deleted");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleToggleStatus = async (p) => {
    const next = p.status === "sold" ? "available" : "sold";
    const verb = next === "sold" ? "Mark as sold" : "Re-list as available";
    if (!confirm(`${verb} — "${p.title}"?`)) return;
    try {
      await api.patch(`/admin/products/${p.id}`, { status: next });
      toast.success(next === "sold" ? "Marked as sold" : "Re-listed as available");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDownloadPdf = async (p, kind) => {
    try {
      const res = await api.get(`/admin/products/${p.id}/${kind}.pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const safe = (p.title || "artwork").replace(/[^a-z0-9-_ ]/gi, "").trim().replace(/\s+/g, "-");
      a.href = url;
      a.download = `${kind}-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't generate PDF");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="label-eyebrow">Artist Studio</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-3 tracking-tight">
              Welcome back{user?.name ? `, ${user.name}` : ""}
            </h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="label-eyebrow text-[#5B5653] hover:text-[#E25C46] flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="border-b border-[#E3DDD7] mb-12 flex gap-10">
          {[
            ["orders", "Orders"],
            ["products", "Artworks"],
            ["profile", "Profile"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`pb-4 label-eyebrow transition-colors ${
                tab === key
                  ? "text-[#2D2926] border-b-2 border-[#E25C46] -mb-px"
                  : "text-[#5B5653] hover:text-[#2D2926]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 label-eyebrow text-[#5B5653]">Loading…</div>
        ) : (
          <>
            {tab === "orders" && <OrdersTable orders={orders} />}
            {tab === "products" && (
              <ProductsList
                products={products}
                onNew={() => setShowNew(true)}
                onEdit={(p) => setEditing(p)}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                onDownloadPdf={handleDownloadPdf}
              />
            )}
            {tab === "profile" && (
              <ProfileEditor profile={profile} onSaved={(p) => setProfile(p)} />
            )}
          </>
        )}
      </div>

      {showNew && (
        <ProductModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            fetchAll();
          }}
        />
      )}
      {editing && (
        <ProductModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
}

function OrdersTable({ orders }) {
  if (orders.length === 0) {
    return (
      <div
        data-testid="orders-empty"
        className="text-center py-20 border border-[#E3DDD7] bg-[#EFEBE6]/40"
      >
        <p className="label-eyebrow mb-3">No orders yet</p>
        <p className="text-[#5B5653] text-sm">
          When an artwork is purchased, the buyer's details will appear here.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="orders-table" className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#E3DDD7]">
            <th className="label-eyebrow py-4 pr-6">Date</th>
            <th className="label-eyebrow py-4 pr-6">Artwork</th>
            <th className="label-eyebrow py-4 pr-6">Customer</th>
            <th className="label-eyebrow py-4 pr-6">Address</th>
            <th className="label-eyebrow py-4 pr-6 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr
              key={o.id}
              data-testid={`order-row-${o.id}`}
              className="border-b border-[#E3DDD7] hover:bg-[#EFEBE6]/40"
            >
              <td className="py-5 pr-6 text-sm text-[#5B5653] align-top">
                {new Date(o.created_at).toLocaleDateString("en-AU", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </td>
              <td className="py-5 pr-6 align-top">
                <span className="font-serif text-lg text-[#2D2926]">{o.product_title}</span>
              </td>
              <td className="py-5 pr-6 align-top text-sm text-[#2D2926]">
                <div>{o.customer_name}</div>
                <div className="text-xs text-[#5B5653]">{o.customer_email}</div>
                {o.customer_phone && (
                  <div className="text-xs text-[#5B5653]">{o.customer_phone}</div>
                )}
              </td>
              <td className="py-5 pr-6 align-top text-sm text-[#5B5653]">
                <div>{o.address_line1}</div>
                {o.address_line2 && <div>{o.address_line2}</div>}
                <div>
                  {o.city}, {o.state} {o.postcode}
                </div>
                <div>{o.country}</div>
              </td>
              <td className="py-5 pr-6 align-top text-right font-serif text-lg text-[#2D2926]">
                {formatPrice(o.amount, (o.currency || "aud").toUpperCase())}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductsList({ products, onNew, onEdit, onDelete, onToggleStatus, onDownloadPdf }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-[#5B5653]">
          {products.length} {products.length === 1 ? "artwork" : "artworks"} •{" "}
          {products.filter((p) => p.status === "available").length} available
        </p>
        <button data-testid="new-product-btn" onClick={onNew} className="btn-primary">
          <Plus className="w-4 h-4 mr-2 inline" /> New Artwork
        </button>
      </div>
      {products.length === 0 ? (
        <div className="text-center py-20 border border-[#E5D8BD] bg-[#EFEBE6]/40">
          <p className="label-eyebrow mb-3">No artworks yet</p>
          <p className="text-[#5B5653] text-sm">Upload your first piece to begin.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="flex flex-col gap-3 border border-[#E5D8BD] bg-[#F8F5F2] p-4"
            >
              <div className="bg-[#EFEBE6] aspect-[4/5] overflow-hidden relative">
                <img
                  src={imageUrl(p.image_path)}
                  alt={p.title}
                  className="w-full h-full object-cover"
                />
                {p.status === "sold" && (
                  <span className="absolute top-3 left-3 bg-[#2D2926] text-[#F8F5F2] text-xs px-3 py-1 uppercase tracking-widest">
                    Sold
                  </span>
                )}
              </div>
              <div>
                <p className="font-serif text-lg text-[#2D2926]">{p.title}</p>
                <p className="text-xs text-[#5B5653]">
                  {p.size} • {formatPrice(p.price)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  data-testid={`edit-product-${p.id}`}
                  onClick={() => onEdit(p)}
                  className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-[#2D2926] text-[#2D2926] hover:bg-[#2D2926] hover:text-[#F8F5F2] transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" /> Edit
                </button>
                {p.status === "available" ? (
                  <button
                    data-testid={`mark-sold-${p.id}`}
                    onClick={() => onToggleStatus(p)}
                    className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-[#3E6649] text-[#3E6649] hover:bg-[#3E6649] hover:text-[#F8F5F2] transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark Sold
                  </button>
                ) : (
                  <button
                    data-testid={`relist-${p.id}`}
                    onClick={() => onToggleStatus(p)}
                    className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-[#E25C46] text-[#E25C46] hover:bg-[#E25C46] hover:text-[#F8F5F2] transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Re-list
                  </button>
                )}
                <button
                  data-testid={`delete-product-${p.id}`}
                  onClick={() => onDelete(p.id)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-[#B73225] text-[#B73225] hover:bg-[#B73225] hover:text-[#F8F5F2] transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 pt-1 border-t border-[#E5D8BD] mt-1 pt-3">
                <button
                  data-testid={`story-pdf-${p.id}`}
                  onClick={() => onDownloadPdf(p, "story")}
                  className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider bg-[#EFEBE6] text-[#2D2926] hover:bg-[#E5D8BD] transition-colors"
                  title="Download the story as a printable PDF"
                >
                  <FileText className="w-3.5 h-3.5" /> Story PDF
                </button>
                <button
                  data-testid={`certificate-pdf-${p.id}`}
                  onClick={() => onDownloadPdf(p, "certificate")}
                  className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider bg-[#EFEBE6] text-[#2D2926] hover:bg-[#E5D8BD] transition-colors"
                  title="Download the Certificate of Authenticity as a PDF"
                >
                  <BadgeCheck className="w-3.5 h-3.5" /> Certificate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  // Normalise legacy single-image products into the array form so editing always works.
  const initialImages = (() => {
    const list = (product?.image_paths || []).filter(Boolean);
    if (list.length === 0 && product?.image_path) return [product.image_path];
    return list;
  })();
  const [form, setForm] = useState({
    title: product?.title || "",
    price: product?.price || "",
    size: product?.size || "",
    story: product?.story || "",
  });
  const [images, setImages] = useState(initialImages);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const MAX_IMAGES = 5;
  const slotsLeft = MAX_IMAGES - images.length;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > slotsLeft) {
      toast.error(`You can only add ${slotsLeft} more ${slotsLeft === 1 ? "photo" : "photos"} (max ${MAX_IMAGES}).`);
    }
    const toUpload = files.slice(0, slotsLeft);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const data = new FormData();
        data.append("file", file);
        const res = await api.post("/upload", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.path);
      }
      setImages((prev) => [...prev, ...uploaded].slice(0, MAX_IMAGES));
      if (uploaded.length > 0) {
        toast.success(uploaded.length === 1 ? "Photo added" : `${uploaded.length} photos added`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const makePrimary = (idx) => {
    if (idx === 0) return;
    setImages((prev) => {
      const next = [...prev];
      const [chosen] = next.splice(idx, 1);
      return [chosen, ...next];
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      toast.error("Please upload at least one photo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        image_path: images[0],
        image_paths: images,
      };
      if (product) {
        await api.patch(`/admin/products/${product.id}`, payload);
        toast.success("Artwork updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Artwork added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="product-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2D2926]/40 backdrop-blur-sm p-4 overflow-y-auto"
    >
      <div className="bg-[#F8F5F2] w-full max-w-2xl my-8 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center px-8 py-6 border-b border-[#E3DDD7] sticky top-0 bg-[#F8F5F2]">
          <h2 className="font-serif text-2xl text-[#2D2926]">
            {product ? "Edit Artwork" : "New Artwork"}
          </h2>
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="p-1 hover:text-[#E25C46]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSave} className="p-8 space-y-8">
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <span className="label-eyebrow">Photos</span>
              <span className="text-xs text-[#5B5653]">
                {images.length} / {MAX_IMAGES} · first photo is the cover
              </span>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {images.map((path, idx) => (
                <div
                  key={path + idx}
                  data-testid={`product-image-slot-${idx}`}
                  className="relative aspect-[4/5] bg-[#EFEBE6] overflow-hidden group"
                >
                  <img
                    src={imageUrl(path)}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {idx === 0 && (
                    <span className="absolute top-1.5 left-1.5 bg-[#E25C46] text-[#F8F5F2] text-[9px] uppercase tracking-widest px-1.5 py-0.5">
                      Cover
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
                    {idx !== 0 && (
                      <button
                        type="button"
                        data-testid={`make-primary-${idx}`}
                        onClick={() => makePrimary(idx)}
                        className="flex-1 text-[10px] uppercase tracking-widest bg-[#2D2926]/85 text-[#F8F5F2] py-1.5 hover:bg-[#2D2926]"
                      >
                        Set cover
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`remove-image-${idx}`}
                      onClick={() => removeImage(idx)}
                      className="flex-1 text-[10px] uppercase tracking-widest bg-[#B73225]/90 text-[#F8F5F2] py-1.5 hover:bg-[#B73225]"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  data-testid="upload-image-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="aspect-[4/5] border border-dashed border-[#5B5653]/40 flex flex-col items-center justify-center gap-2 text-[#5B5653] hover:text-[#E25C46] hover:border-[#E25C46] transition-colors disabled:opacity-60"
                >
                  {uploading ? (
                    <span className="text-[10px] uppercase tracking-widest">Uploading…</span>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span className="text-[10px] uppercase tracking-widest">Add photo</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <input
              ref={fileRef}
              data-testid="product-image-input"
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              className="hidden"
            />
            <p className="text-xs text-[#5B5653] mt-3">
              Up to {MAX_IMAGES} photos. JPG, PNG or WebP. The first photo is shown as the cover
              everywhere — hover a photo to set it as cover or remove it.
            </p>
          </div>

          <label className="block">
            <span className="label-eyebrow block mb-2">Title</span>
            <input
              data-testid="product-title-input"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-line"
            />
          </label>

          <div className="grid grid-cols-2 gap-6">
            <label className="block">
              <span className="label-eyebrow block mb-2">Price (AUD)</span>
              <input
                data-testid="product-price-input"
                required
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="input-line"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-2">Size</span>
              <input
                data-testid="product-size-input"
                required
                placeholder="e.g. 60 × 90 cm"
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="input-line"
              />
            </label>
          </div>

          <label className="block">
            <span className="label-eyebrow block mb-2">The Story</span>
            <textarea
              data-testid="product-story-input"
              required
              rows={6}
              value={form.story}
              onChange={(e) => setForm({ ...form, story: e.target.value })}
              className="input-line resize-none"
            />
          </label>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              data-testid="save-product-btn"
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
            >
              {saving ? "Saving…" : product ? "Save Changes" : "Publish Artwork"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProfileEditor({ profile, onSaved }) {
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const fileRef = useRef();
  const heroRef = useRef();

  useEffect(() => {
    setForm(profile);
  }, [profile]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: res.data.path }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const handleHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, hero_image_path: res.data.path }));
      toast.success("Home wallpaper uploaded — remember to Save");
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploadingHero(false);
  };

  const clearHero = () => {
    setForm((f) => ({ ...f, hero_image_path: "" }));
    toast.success("Wallpaper reset to default — remember to Save");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put("/admin/artist-profile", form);
      onSaved(res.data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  return (
    <form data-testid="profile-editor" onSubmit={handleSave} className="max-w-3xl space-y-10">
      <div>
        <span className="label-eyebrow block mb-3">Home wallpaper</span>
        <p className="text-xs text-[#5B5653] mb-4 max-w-xl">
          This is the artwork shown behind the headline on the home page. Use a colourful,
          high-resolution image — landscape works best.
        </p>
        <div className="flex items-start gap-6">
          <div className="w-56 h-32 bg-[#EFEBE6] flex items-center justify-center overflow-hidden flex-shrink-0 border border-[#E5D8BD]">
            {form.hero_image_path ? (
              <img
                src={imageUrl(form.hero_image_path)}
                alt="hero preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#5B5653]">
                Using default
              </span>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <input
              ref={heroRef}
              data-testid="hero-image-input"
              type="file"
              accept="image/*"
              onChange={handleHero}
              className="hidden"
            />
            <button
              type="button"
              data-testid="upload-hero-btn"
              onClick={() => heroRef.current?.click()}
              disabled={uploadingHero}
              className="btn-secondary"
            >
              <Upload className="w-4 h-4 inline mr-2" />
              {uploadingHero ? "Uploading…" : "Change Wallpaper"}
            </button>
            {form.hero_image_path && (
              <button
                type="button"
                data-testid="reset-hero-btn"
                onClick={clearHero}
                className="text-xs text-[#5B5653] hover:text-[#E25C46] underline underline-offset-4 self-start"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <span className="label-eyebrow block mb-3">Profile photo</span>
        <div className="flex items-start gap-6">
          <div className="w-36 h-44 bg-[#EFEBE6] flex items-center justify-center overflow-hidden flex-shrink-0">
            {form.photo_path ? (
              <img
                src={imageUrl(form.photo_path)}
                alt="artist"
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="w-8 h-8 text-[#5B5653]" />
            )}
          </div>
          <div>
            <input
              ref={fileRef}
              data-testid="profile-photo-input"
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              className="hidden"
            />
            <button
              type="button"
              data-testid="profile-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-secondary"
            >
              <Upload className="w-4 h-4 inline mr-2" />
              {uploading ? "Uploading…" : "Upload Photo"}
            </button>
          </div>
        </div>
      </div>

      <label className="block">
        <span className="label-eyebrow block mb-2">Artist name</span>
        <input
          data-testid="profile-name-input"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="input-line"
        />
      </label>

      <label className="block">
        <span className="label-eyebrow block mb-2">Short statement</span>
        <input
          data-testid="profile-statement-input"
          value={form.statement || ""}
          placeholder="A single sentence shown on the homepage"
          onChange={(e) => setForm({ ...form, statement: e.target.value })}
          className="input-line"
        />
      </label>

      <label className="block">
        <span className="label-eyebrow block mb-2">Biography</span>
        <textarea
          data-testid="profile-bio-input"
          rows={8}
          value={form.bio || ""}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          className="input-line resize-none"
        />
      </label>

      <button
        data-testid="save-profile-btn"
        type="submit"
        disabled={saving}
        className="btn-primary"
      >
        {saving ? "Saving…" : "Save Profile"}
      </button>

      <ChangePasswordPanel />
    </form>
  );
}

function ChangePasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
    setSaving(false);
  };

  const PwField = ({ label, value, onChange, which, testid, autoComplete }) => (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <div className="relative">
        <input
          data-testid={testid}
          type={show[which] ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoComplete={autoComplete}
          className="input-line pr-10"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={() => setShow((s) => ({ ...s, [which]: !s[which] }))}
          aria-label={show[which] ? "Hide" : "Show"}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-[#5B5653] hover:text-[#E25C46]"
        >
          {show[which] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );

  return (
    <div
      data-testid="change-password-panel"
      className="mt-16 pt-12 border-t border-[#E5D8BD]"
    >
      <div className="flex items-center gap-3 mb-3">
        <KeyRound className="w-4 h-4 text-[#E25C46]" />
        <p className="label-eyebrow">Change Password</p>
      </div>
      <p className="text-xs text-[#5B5653] mb-8 max-w-xl">
        Passwords are stored securely (one-way encrypted) — for safety, even Colleen can't see
        the existing password. You can change it any time below. Use the eye icon to reveal
        what you've typed.
      </p>
      <div className="space-y-6 max-w-md">
        <PwField
          label="Current password"
          value={current}
          onChange={setCurrent}
          which="current"
          testid="current-password-input"
          autoComplete="current-password"
        />
        <PwField
          label="New password"
          value={next}
          onChange={setNext}
          which="next"
          testid="new-password-input"
          autoComplete="new-password"
        />
        <PwField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          which="confirm"
          testid="confirm-password-input"
          autoComplete="new-password"
        />
        <p className="text-xs text-[#5B5653]">Minimum 8 characters.</p>
        <button
          type="button"
          data-testid="save-password-btn"
          onClick={submit}
          disabled={saving || !current || !next || !confirm}
          className="btn-primary"
        >
          {saving ? "Updating…" : "Update Password"}
        </button>
      </div>
    </div>
  );
}
